import React, { useState } from "react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, Legend, BarChart, Bar,
} from "recharts";
import { toast } from "sonner";
import { AlertTriangle, Printer, TrendingUp, TrendingDown, Download } from "lucide-react";
import { api, apiError, downloadPdf } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { useI18n } from "@/contexts/I18nContext";
import { StatCard, Section, EmptyState, LoadError } from "@/components/Primitives";
import { StaggerRoot } from "@/components/motion";

export default function ReportMonthly() {
  const { t, money, lang } = useI18n();
  const cur = new Date().toISOString().slice(0, 7);
  const [month, setMonth] = useState(cur);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["report-monthly", month],
    queryFn: () => api.get("/reports/monthly", { params: { month } }).then((r) => r.data),
    retry: 1,
  });

  const handleDownloadPdf = async () => {
    try {
      await downloadPdf(`/reports/monthly.pdf?month=${month}&lang=${lang}`, `monthly-${month}.pdf`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (isLoading) return <div className="p-10 text-gray-500">{t("loading")}</div>;
  if (isError) return <LoadError message={apiError(error)} onRetry={() => refetch()} />;

  return (
    <StaggerRoot className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 font-heading">{t("monthlyProfit")}</h1>
          <p className="text-sm text-gray-500 mt-1">{month}</p>
        </div>
        <div className="flex gap-2">
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-11 px-3 rounded-xl border border-gray-200 bg-white text-sm" />
          <button data-testid="monthly-pdf" onClick={handleDownloadPdf} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#071A33] text-white text-sm font-medium hover:bg-[#7F1020]">
            <Download className="w-4 h-4" /> PDF
          </button>
          <button onClick={() => window.print()} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-gray-200 text-sm font-medium hover:bg-gray-50">
            <Printer className="w-4 h-4" /> {t("print")}
          </button>
        </div>
      </div>

      <>
          {data.expenses > data.revenue && (
            <div className="rp-card p-4 border-rose-200 bg-rose-50/50 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
              <div>
                <div className="font-bold text-rose-700">{t("error")}</div>
                <div className="text-sm text-rose-700/80">مصارف بیش از درآمد است.</div>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard icon={TrendingUp} label={t("revenue")} value={money(data.revenue)} accent="#071A33" />
            <StatCard icon={TrendingUp} label={lang === "pashto" ? "نقد ترلاسه شوی" : "نقد دریافت‌شده"} value={money(data.cash_collected || 0)} accent="#16A34A" />
            <StatCard icon={TrendingDown} label={t("expenses")} value={money(data.expenses)} accent="#DC2626" />
            <StatCard icon={TrendingDown} label="COGS" value={money(data.cogs || 0)} accent="#64748B" />
            <StatCard icon={TrendingUp} label={t("grossProfit")} value={money(data.gross_profit)} accent="#16A34A" />
            <StatCard icon={TrendingUp} label={t("netProfit")} value={money(data.net_profit)} accent={data.net_profit < 0 ? "#DC2626" : "#16A34A"} sub={`${t("profitMargin")} ${data.margin.toFixed(1)}%`} />
          </div>

          <Section title={t("revenueExpense")}>
            {(data.trend || []).length === 0 ? <EmptyState title={t("empty")} /> : (
              <div style={{ width: "100%", height: 320 }}>
                <ResponsiveContainer>
                  <AreaChart data={data.trend}>
                    <defs>
                      <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#071A33" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#071A33" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="exp" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#DC2626" stopOpacity={0.25} />
                        <stop offset="95%" stopColor="#DC2626" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="#f3f4f6" vertical={false} />
                    <XAxis dataKey="date" stroke="#9ca3af" fontSize={11} tickFormatter={(d) => d.slice(5)} />
                    <YAxis stroke="#9ca3af" fontSize={11} />
                    <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 12, fontSize: 12, direction: "rtl" }} />
                    <Legend />
                    <Area name={t("revenue")} dataKey="revenue" stroke="#071A33" strokeWidth={2.5} fill="url(#rev)" />
                    <Area name={t("expenses")} dataKey="expense" stroke="#DC2626" strokeWidth={2.5} fill="url(#exp)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </Section>

          <Section title={t("expenses") + " - " + t("byCategory")}>
            {(data.expense_by_category || []).length === 0 ? <EmptyState title={t("empty")} /> : (
              <div style={{ width: "100%", height: 280 }}>
                <ResponsiveContainer>
                  <BarChart data={data.expense_by_category} layout="vertical">
                    <CartesianGrid stroke="#f3f4f6" horizontal={false} />
                    <XAxis type="number" stroke="#9ca3af" fontSize={11} />
                    <YAxis type="category" dataKey="category" stroke="#9ca3af" fontSize={12} width={140} />
                    <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 12, fontSize: 12, direction: "rtl" }} />
                    <Bar dataKey="amount" fill="#DC2626" radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Section>
        </>
    </StaggerRoot>
  );
}
